import React, { useState, useEffect, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { doc, getDoc } from 'firebase/firestore';
import { db, functions } from '../firebase/config';
import { TerraDevice } from '../types/user';
import { getResourceDisplayName, getResourceIcon } from '../utils/wearableUtils';
import { ArrowTopRightOnSquareIcon, ClipboardDocumentIcon, ClockIcon, DevicePhoneMobileIcon, ExclamationCircleIcon, MoonIcon, XMarkIcon } from '@heroicons/react/24/outline';

interface WearableDetailsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  user: {
    id: string;
    name: string;
    surname: string;
    email: string;
  };
  onDeviceDeauthed?: () => void;
}

const WearableDetailsDialog: React.FC<WearableDetailsDialogProps> = ({
  isOpen,
  onClose,
  user,
  onDeviceDeauthed,
}) => {
  const [devices, setDevices] = useState<TerraDevice[]>([]);
  const [devicesLoading, setDevicesLoading] = useState(false);
  const [copiedDeviceId, setCopiedDeviceId] = useState<string | null>(null);
  const [deauthConfirm, setDeauthConfirm] = useState<{ deviceId: string; resource: string } | null>(null);
  const [deauthLoading, setDeauthLoading] = useState(false);
  const [deauthResult, setDeauthResult] = useState<{ success: boolean; message: string } | null>(null);

  const fetchDevices = useCallback(async () => {
    setDevicesLoading(true);
    try {
      const privateRef = doc(db, 'users', user.id, 'private', 'data');
      const privateSnap = await getDoc(privateRef);
      if (privateSnap.exists()) {
        const data = privateSnap.data();
        setDevices((data?.deviceIDs || []) as TerraDevice[]);
      } else {
        setDevices([]);
      }
    } catch (error) {
      console.error('Failed to fetch user devices:', error);
    } finally {
      setDevicesLoading(false);
    }
  }, [user.id]);

  // Fetch fresh devices when the dialog opens
  useEffect(() => {
    if (isOpen) {
      fetchDevices();
    }
  }, [isOpen, fetchDevices]);

  if (!isOpen) return null;

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }).format(date);
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const activeDevices = devices.filter(device => !device.expired);
  const expiredDevices = devices.filter(device => device.expired);

  const handleDeviceClick = async (deviceId: string) => {
    try {
      // Copy device ID to clipboard
      await navigator.clipboard.writeText(deviceId);
      setCopiedDeviceId(deviceId);
      
      // Clear the copied state after 2 seconds
      setTimeout(() => {
        setCopiedDeviceId(null);
      }, 2000);
      
      // Open Terra dashboard in new tab
      window.open('https://dashboard.tryterra.co/dashboard/users', '_blank');
    } catch (error) {
      console.error('Failed to copy device ID to clipboard:', error);
      // Fallback: still open the dashboard even if clipboard fails
      window.open('https://dashboard.tryterra.co/dashboard/users', '_blank');
    }
  };

  const handleSeeSleepData = (deviceId: string) => {
    // Generate the Google Cloud Storage URL for sleep data filtered by device ID
    // The pageState parameter contains URL-encoded filter information
    const encodedDeviceId = encodeURIComponent(`"${deviceId}"`);
    const pageStateFilter = `%22StorageObjectListTable%22:%22f%22:%22%255B%257B_22k_22_3A_22_22_2C_22t_22_3A10_2C_22v_22_3A_22_5C_22${encodedDeviceId}_5C_22_22%257D%255D%22`;
    
    const sleepDataUrl = `https://console.cloud.google.com/storage/browser/terra-production/2022-03-16/sleep;tab=objects?pageState=(${pageStateFilter})&project=peak-x-937b6&prefix=${deviceId}&forceOnObjectsSortingFiltering=false`;
    
    window.open(sleepDataUrl, '_blank');
  };

  const handleDeauth = async () => {
    if (!deauthConfirm) return;

    setDeauthLoading(true);
    setDeauthResult(null);

    try {
      const adminDeauthDevice = httpsCallable(functions, 'adminDeauthDevice');
      const result = await adminDeauthDevice({
        targetUserId: user.id,
        deviceId: deauthConfirm.deviceId,
      });

      const data = result.data as { success: boolean; message: string };

      setDeauthResult({ success: true, message: data.message });

      // Re-fetch the user's devices to get the updated list
      await fetchDevices();
      onDeviceDeauthed?.();

      // Close confirmation after a short delay
      setTimeout(() => {
        setDeauthConfirm(null);
        setDeauthResult(null);
      }, 2000);
    } catch (error: any) {
      console.error('Error deauthing device:', error);
      setDeauthResult({
        success: false,
        message: error.message || 'Failed to deauthenticate device',
      });
    } finally {
      setDeauthLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-4 bg-black bg-opacity-50">
        <div className="bg-white rounded-t-lg sm:rounded-lg shadow-xl max-w-lg w-full">
            {/* Header */}
            <div className="px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-3">
                        <DevicePhoneMobileIcon className="w-6 h-6 text-primary" />
                        <h3 className="text-lg font-medium text-gray-900">
                        Wearable Devices
                        </h3>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                        <XMarkIcon className="w-5 h-5" />
                    </button>
                </div>

                {/* User info */}
                <div className="mb-6 p-3 bg-gray-50 rounded-lg">
                    <p className="font-medium text-sm text-gray-900">
                        User Email: {user.email}
                    </p>
                    <p className="text-sm text-gray-500">User ID: {user.id}</p>
                </div>

                {/* Devices list */}
                <div className="space-y-4">
                    {/* Loading state */}
                    {devicesLoading && devices.length === 0 && (
                        <div className="text-center py-8">
                            <span className="loading loading-spinner loading-md text-primary"></span>
                                <p className="text-sm text-gray-500 mt-2">Loading devices...</p>
                            </div>
                    )}

                    {/* Active devices */}
                    {activeDevices.length > 0 && (
                    <div>
                        <h4 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
                            <div className="size-3 animate-pulse bg-green-500 rounded-full mr-2 outline outline-green-100"></div>
                            Active Devices ({activeDevices.length})
                        </h4>
                        <div className="space-y-2">
                            {activeDevices.map((device, index) => (
                            <div 
                                key={`${device.id}-${index}`}
                                className="border border-green-200 bg-green-50 rounded-lg relative"
                            >
                                <div className="absolute top-2 right-2 flex items-center space-x-1">
                                    <button
                                        onClick={(e) => {
                                        e.stopPropagation();
                                        handleSeeSleepData(device.id);
                                        }}
                                        className="btn btn-xs btn-circle btn-ghost text-green-700 hover:bg-green-200"
                                        title="View sleep data in Google Cloud Storage"
                                    >
                                        <MoonIcon className="w-4 h-4" />
                                    </button>
                                    <button
                                        onClick={(e) => {
                                        e.stopPropagation();
                                        setDeauthResult(null);
                                        setDeauthConfirm({ deviceId: device.id, resource: device.resource });
                                        }}
                                        className="btn btn-xs btn-circle btn-ghost text-red-500 hover:bg-red-100"
                                        title="Disconnect this device"
                                    >
                                        <XMarkIcon className="w-4 h-4" />
                                    </button>
                                </div>
                                <div 
                                    onClick={() => handleDeviceClick(device.id)}
                                    className="flex items-center justify-between p-3 cursor-pointer hover:bg-green-100 transition-all duration-200 group"
                                    title="Click to copy device ID and open Terra dashboard"
                                >
                                    <div className="flex items-center space-x-3">
                                        <span className="text-lg">{getResourceIcon(device.resource)}</span>
                                    <div>
                                    <p className="font-medium text-gray-900 group-hover:text-green-800">
                                        {getResourceDisplayName(device.resource)}
                                    </p>
                                    <div className="flex items-center gap-1 text-xs text-gray-500">
                                        <ClockIcon className="w-3 h-3 mr-1" />
                                        <p>
                                        Connected: {formatDate(device.connectedAt)}
                                        </p>
                                    </div>
                                        <p className="text-xs text-gray-500 font-mono line-clamp-1">
                                        ID: {device.id}
                                        </p>
                                    </div>
                                </div>
                                </div>
                            </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Expired devices */}
                {expiredDevices.length > 0 && (
                    <div>
                    <h4 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
                        <div className="size-3 bg-red-500 rounded-full mr-2 outline outline-red-100"></div>
                        Expired Devices ({expiredDevices.length})
                    </h4>
                    <div className="space-y-2">
                        {expiredDevices.map((device, index) => (
                        <div
                            key={`${device.id}-${index}`}
                            className="border border-red-200 bg-red-50 rounded-lg relative opacity-80"
                        >
                            <div className="absolute top-2 right-2 flex items-center space-x-1">
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleSeeSleepData(device.id);
                                    }}
                                    className="btn btn-xs btn-circle btn-ghost text-red-700 hover:bg-red-200"
                                    title="View sleep data in Google Cloud Storage"
                                >
                                    <MoonIcon className="w-4 h-4" />
                                </button>
                            </div>
                            <div
                            onClick={() => handleDeviceClick(device.id)}
                            className="flex items-center justify-between p-3 cursor-pointer hover:bg-red-100 transition-all duration-200 group"
                            title="Click to copy device ID and open Terra dashboard"
                            >
                            <div className="flex items-center space-x-3">
                                <span className="text-lg">{getResourceIcon(device.resource)}</span>
                                <div>
                                <p className="font-medium text-gray-900 group-hover:text-red-800">
                                    {getResourceDisplayName(device.resource)}
                                </p>
                                <div className="flex items-center gap-1 text-xs text-gray-500">
                                    <ClockIcon className="w-3 h-3 mr-1" />
                                    <p>Connected: {formatDate(device.connectedAt)} · <span className="text-red-500">Expired</span></p>
                                </div>
                                <p className="text-xs text-gray-500 font-mono line-clamp-1">
                                    ID: {device.id}
                                </p>
                                </div>
                            </div>
                            </div>
                        </div>
                        ))}
                    </div>
                    </div>
                )}

                {/* No devices */}
                {!devicesLoading && devices.length === 0 && (
                    <div className="text-center py-8">
                    <DevicePhoneMobileIcon className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">No wearable devices found</p>
                    <p className="text-sm text-gray-400">This user hasn't connected any devices yet</p>
                    </div>
                )}
                </div>
            </div>

            {/* Footer */}
            <div className="bg-gray-50 rounded-b-lg px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                type="button"
                onClick={onClose}
                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary text-base font-medium text-white hover:bg-primary-focus focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary sm:ml-3 sm:w-auto sm:text-sm"
                >
                Close
                </button>
            </div>
        </div>

      {/* Deauth Confirmation Dialog */}
      {deauthConfirm && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center">
          <div className="fixed inset-0 bg-black bg-opacity-40" onClick={() => { if (!deauthLoading) { setDeauthConfirm(null); setDeauthResult(null); } }} />
          <div className="relative bg-white rounded-lg shadow-xl max-w-sm w-full mx-4 p-6">
            <div className="flex items-center space-x-3 mb-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                <ExclamationCircleIcon className="w-5 h-5 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Disconnect Device</h3>
            </div>

            <p className="text-sm text-gray-600 mb-2">
              Are you sure you want to disconnect <span className="font-medium">{getResourceDisplayName(deauthConfirm.resource)}</span> for{' '}
              <span className="font-medium">{user.name} {user.surname}</span>?
            </p>
            <p className="text-xs text-gray-400 mb-1 font-mono">Device ID: {deauthConfirm.deviceId}</p>
            <p className="text-xs text-red-500 mb-5">
              This will deauthenticate the device via Terra and mark it as expired. The user will need to reconnect.
            </p>

            {deauthResult && (
              <div className={`mb-4 p-3 rounded-lg text-sm ${deauthResult.success ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                {deauthResult.message}
              </div>
            )}

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => { setDeauthConfirm(null); setDeauthResult(null); }}
                disabled={deauthLoading}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDeauth}
                disabled={deauthLoading || deauthResult?.success === true}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50 flex items-center space-x-2"
              >
                {deauthLoading ? (
                  <>
                    <span className="loading loading-spinner loading-xs"></span>
                    <span>Disconnecting...</span>
                  </>
                ) : (
                  <span>Disconnect</span>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WearableDetailsDialog;
